import { useMemo } from 'react';
import { Configuration, OrdersApi } from '@/api';
import { OrderVarianceFilters } from '@/api/OrdersApi';
import { StoreOrderVariance } from '@/models/OrderVariance';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';

const DEFAULT_FILTERS: OrderVarianceFilters = {
  varianceThresholdPercent: '50',
};

const useStoreOrderVariancesQuery = (
  orderIds: string[],
  filters: OrderVarianceFilters = DEFAULT_FILTERS,
) => {
  const auth = useAuth();
  const ordersApi = useMemo(() => new OrdersApi(new Configuration(auth)), [auth]);
  const effectiveFilters = { ...DEFAULT_FILTERS, ...filters };

  const query = useQuery({
    queryKey: ['store-order-variances', orderIds, effectiveFilters],
    enabled: orderIds.length > 0,
    queryFn: () =>
      ordersApi.listStoreOrderVariances(
        orderIds,
        effectiveFilters,
        0,
        Math.max(orderIds.length, 1),
      ),
  });

  const variancesByOrderId = useMemo(() => {
    const result = new Map<string, StoreOrderVariance>();

    for (const variance of query.data?.items ?? []) {
      result.set(variance.orderId, variance);
    }

    return result;
  }, [query.data?.items]);

  return {
    variances: query.data?.items ?? [],
    variancesByOrderId,
    totalCount: query.data?.totalCount ?? query.data?.estimatedTotalCount ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
};

export default useStoreOrderVariancesQuery;
